<template>
  <!-- 教學頁頁首（2026-10-16）：每一個點是一位在 Hahow 上過課的同學，四門課的人數照公開 API 的快照，一個都不少。
       點沿著勞倫茲吸引子走（哲宇 16:03：「這邊我想要做成大型勞倫茲引子」；同一天上午的第一版是平面流場）：
       每個點是一組 (x, y, z)，照勞倫茲方程式一步一步積分，起點不同、軌道各自分開，但都被吸到同一隻蝴蝶上。
       鏡頭停在兩個漩渦左右並排的那一面，左右小幅擺動看得出立體，不會轉到側面變成一條線；橫向拉長 1.3 倍。
       每一格畫的是點從上一格走到這一格的那一小段線，加上很淡的拖尾，軌跡慢慢流過去（16:1x 哲宇：「慢一點、更有質感、橫向拉長一點、慢慢流下一點點軌跡」）。
       滑鼠或手指靠近，點在畫面上被推開，慢慢回到軌道。減少動態：只畫一張靜止的。捲出視窗或切到背景就停。預渲染只留外框，畫布在瀏覽器裡才畫。 -->
  <div ref="host" class="tf" :class="{ 'is-live': live }">
    <canvas ref="cv" class="tf-canvas" aria-hidden="true"></canvas>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { prefersReducedMotion } from '@/runtime/view-transition'

const props = defineProps({
  // [{ key, count, color, alpha, size, pulse, keep }]：畫的順序＝陣列順序，後畫的疊在上面（這一期放最後）；keep＝亮度不隨密度壓
  groups: { type: Array, required: true },
  seed: { type: Number, default: 20160901 },
})

const host = ref(null), cv = ref(null), live = ref(false)
let ctx = null, W = 0, H = 0, dpr = 1
let X = null, Y = null, Z = null, grp = null, SX = null, SY = null, PX = null, PY = null, OX = null, OY = null, total = 0, fresh = true
let raf = 0, running = false, io = null, ro = null, t0 = 0
const pointer = { x: -1e4, y: -1e4, on: false }

// 勞倫茲方程式（σ＝10、ρ＝28、β＝8/3，他 1963 年那一組），每格一步
const SIGMA = 10, RHO = 28, BETA = 8 / 3, DT = 0.002
// 鏡頭：繞直立的 z 軸看，θ 在 1.85 附近擺 ±0.3（兩個漩渦並排、最寬的那一面），一點點透視
const TH0 = 1.85, SWAY = 0.3, PERSP = 0.003, STRETCH = 1.3

// 可重現的亂數（mulberry32）：每次進頁點的起點都一樣，截圖可以比對
const rng = (s) => () => { s |= 0; s = (s + 0x6d2b79f5) | 0; let r = Math.imul(s ^ (s >>> 15), 1 | s); r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r; return ((r ^ (r >>> 14)) >>> 0) / 4294967296 }

const step = (dt = DT) => {
  for (let i = 0; i < total; i++) {
    const x = X[i], y = Y[i], z = Z[i]
    X[i] = x + SIGMA * (y - x) * dt
    Y[i] = y + (x * (RHO - z) - y) * dt
    Z[i] = z + (x * y - BETA * z) * dt
  }
}

const seedPoints = () => {
  total = props.groups.reduce((n, g) => n + g.count, 0)
  X = new Float32Array(total); Y = new Float32Array(total); Z = new Float32Array(total); grp = new Uint8Array(total)
  SX = new Float32Array(total); SY = new Float32Array(total); PX = new Float32Array(total); PY = new Float32Array(total); OX = new Float32Array(total); OY = new Float32Array(total)
  const rand = rng(props.seed)
  let i = 0
  props.groups.forEach((g, gi) => { for (let k = 0; k < g.count; k++, i++) { X[i] = (rand() - 0.5) * 40; Y[i] = (rand() - 0.5) * 50; Z[i] = 3 + rand() * 45; grp[i] = gi } })
  // 先用大一點的步長走 300 步（約 1.8 個時間單位）：第一張畫面點就已經落在蝴蝶上（不然開頭是一團雲往裡縮）
  for (let s = 0; s < 300; s++) step(0.006)
}

// 3D → 畫面：吸引子撐滿畫框高度（z 約 0–50），窄螢幕以寬度為準
const project = (t) => {
  const th = TH0 + SWAY * Math.sin(t * 0.00005), c = Math.cos(th), s = Math.sin(th)
  const sc = Math.min(W / (62 * STRETCH), H / 50), cx = W / 2, cy = H / 2
  for (let i = 0; i < total; i++) {
    PX[i] = SX[i]; PY[i] = SY[i]
    const x = X[i], y = Y[i]
    const p = 1 / (1 + (x * s + y * c) * PERSP)
    SX[i] = cx + (x * c - y * s) * sc * STRETCH * p + OX[i]
    SY[i] = cy - (Z[i] - 25) * sc * p + OY[i]
  }
  return sc
}

// 滑鼠／手指：畫面上的位移，推開後每格回彈一點（不改 3D 軌道）
const push = () => {
  const R = 110, R2 = R * R
  for (let i = 0; i < total; i++) {
    let ox = OX[i] * 0.94, oy = OY[i] * 0.94
    if (pointer.on) {
      const dx = SX[i] - pointer.x, dy = SY[i] - pointer.y, d2 = dx * dx + dy * dy
      if (d2 < R2 && d2 > 0.01) { const f = (1 - d2 / R2) * 3 / Math.sqrt(d2); ox += dx * f; oy += dy * f }
    }
    OX[i] = ox; OY[i] = oy
  }
}

const draw = (t, still = false) => {
  // 拖尾：每格蓋一層很淡的黑（0.05），走過的線慢慢暗下去；靜止版先清成全黑
  ctx.fillStyle = still ? '#000' : 'rgba(0,0,0,0.05)'
  ctx.fillRect(0, 0, W, H)
  const sc = project(t)
  if (fresh) { PX.set(SX); PY.set(SY); fresh = false }
  // 兩萬多條線疊在一隻蝴蝶上：依蝴蝶的面積算密度，太密就把線壓暗，金色那一群才看得見
  const dim = Math.min(1, Math.sqrt(0.05 / (total / (40 * sc * STRETCH * 46 * sc * 0.6)))) * 0.75
  props.groups.forEach((g, gi) => {
    let a = g.alpha ?? 1
    if (!g.keep) a *= dim
    ctx.globalAlpha = a
    ctx.strokeStyle = g.color
    ctx.lineWidth = g.size || 0.5
    ctx.beginPath()
    for (let i = 0; i < total; i++) if (grp[i] === gi) { ctx.moveTo(PX[i], PY[i]); ctx.lineTo(SX[i], SY[i]) }
    ctx.stroke()
  })
  ctx.globalAlpha = 1
}

const resize = () => {
  const r = host.value.getBoundingClientRect()
  W = Math.max(1, Math.round(r.width)); H = Math.max(1, Math.round(r.height))
  dpr = Math.min(2, window.devicePixelRatio || 1)
  if (!X) seedPoints()
  fresh = true
  cv.value.width = Math.round(W * dpr); cv.value.height = Math.round(H * dpr)
  ctx = cv.value.getContext('2d', { alpha: false })
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  draw(performance.now() - t0, true)
  // 減少動態：一次走 90 格畫出軌跡就停在那張（不動，但看得到線）
  if (prefersReducedMotion()) for (let k = 0; k < 90; k++) { step(); draw(performance.now() - t0) }
}

// 窄螢幕（手機）隔一格才算一次（每秒 30 格）：兩萬多個點，手機的電池比桌機寶貴
let tick = 0
const frame = (now) => {
  if (!running) return
  if (W >= 700) { step(); push(); draw(now - t0) } else if ((tick++ & 1) === 0) { step(DT * 2); push(); draw(now - t0) }
  raf = requestAnimationFrame(frame)
}
const start = () => { if (running || prefersReducedMotion()) return; running = true; live.value = true; raf = requestAnimationFrame(frame) }
const stop = () => { running = false; cancelAnimationFrame(raf) }
let inView = true
const onVis = () => (document.hidden ? stop() : inView && start())

const onMove = (e) => { const r = host.value.getBoundingClientRect(); pointer.x = e.clientX - r.left; pointer.y = e.clientY - r.top; pointer.on = true }
const onLeave = () => { pointer.on = false }

onMounted(() => {
  if (!host.value || !cv.value) return
  t0 = performance.now()
  resize()
  if (prefersReducedMotion()) return
  host.value.addEventListener('pointermove', onMove, { passive: true })
  host.value.addEventListener('pointerleave', onLeave, { passive: true })
  document.addEventListener('visibilitychange', onVis)
  if (typeof ResizeObserver === 'function') { ro = new ResizeObserver(() => resize()); ro.observe(host.value) }
  if (typeof IntersectionObserver === 'function') {
    io = new IntersectionObserver((es) => { inView = es.some((e) => e.isIntersecting); if (inView && !document.hidden) start(); else stop() }, { rootMargin: '80px 0px' })
    io.observe(host.value)
  } else start()
})
onBeforeUnmount(() => {
  stop(); io?.disconnect(); ro?.disconnect()
  document.removeEventListener('visibilitychange', onVis)
  host.value?.removeEventListener('pointermove', onMove)
  host.value?.removeEventListener('pointerleave', onLeave)
})
</script>

<style>
.tf { position: relative; isolation: isolate; width: 100%; height: 100%; background: #000; overflow: hidden; touch-action: pan-y; }
/* App.vue 有一條全站的 canvas { position: absolute; z-index: -1 }（早期 p5 背景頁留下的），會把畫布壓到黑底後面：這裡蓋回來 */
.tf .tf-canvas { position: absolute; inset: 0; z-index: 0; display: block; width: 100%; height: 100%; }
</style>
